import { IAddress } from '@carm/shared/components/form-elements/models';
import * as dayjs from 'dayjs';
import {
  IBn9RegistrationModel,
  IBn9RegistrationSubmitModel,
  ILegalEntityModel,
  IValidateBusinessModel,
  ILegalNameModel,
  IOwnersModel,
  ILegalEntitySubmitModel,
  IBusinessOwnershipSubmitModel,
  IOwnershipSubmitModel,
  ILegalNameSubmitModel,
  IAddressModel,
  IBusinessAddressSubmitModel,
  IAddressSubmitModel,
  IBusinessPhonesSubmitModel,
  IPhoneSubmitModel,
  IContactsModel,
  IBusinessContactsSubmitModel,
  IContactSubmitModel,
  IBusinessActivityModel,
  IBusinessActivitySubmitModel,
  ITransactionInfoSubmitModel,
  IValidateBusinessInformationSubmitModel,
  IBn9RegistrationDraftRequestModel,
  ILinkBusinessProgram,
  IBusinessRegistrationDraftResponse,
  IGenericContactInfoSubmitModel,
  RegistrationProgramType,
  IOwner,
} from '@carm/_core/business-registration/models';
import { IBusinessOfficial, IProgramResponse, ProgramTypes } from '@carm/_core';
import { mapRegistrationProgramTypeToProgramEnrolmentType } from './program-account-profile.converters';
import { IPhone } from '@carm/_core/business/models/phone.model';
import { IContact } from '@carm/_core/business/models/contact.model';

export function mapModelToDraftRequest(
  model: IBn9RegistrationModel | IValidateBusinessModel
): IBn9RegistrationDraftRequestModel {
  const request = new IBn9RegistrationDraftRequestModel();
  request.uid = model.uid;
  request.businessLegalName = model.legalName?.legalName;
  request.draft = JSON.stringify(model);
  request.scenario = model.scenario;
  return request;
}

export function mapBn9RegistrationSubmitToModel(submit: IBn9RegistrationSubmitModel): IBn9RegistrationModel {
  return {
    legalEntity: {
      businessStructure: submit.legalEntity.businessStructure,
    } as ILegalEntityModel,
  } as IBn9RegistrationModel;
}

export function mapValidateBusinessToBn9RegistrationModel(model: IValidateBusinessModel): IBn9RegistrationModel {
  const registration = {
    uid: model.uid,
    businessNumber: model.businessNumber,
    programType: model.programType,
    programId: model.programId,
    programNumber: model.programNumber,
    legalName: {
      legalName: model.legalEntityName,
    } as ILegalNameModel,
    address: {
      physicalAddress: {
        line1: model.address.line1,
        line2: model.address.line2,
        carmCountry: model.address.country,
        carmRegion: model.address.provinceOrState,
        postalCode: model.address.postalOrZipCode,
        town: model.address.city,
      },
    },
    legalEntity: {
      businessStructure: model.businessStructure,
    } as ILegalEntityModel,
    owners: {
      authorizedOfficial1: model.authorizedOfficial1,
      authorizedOfficial2: model.authorizedOfficial2,
    } as IOwnersModel,
    contacts: model.contacts,
    scenario: model.scenario
  } as IBn9RegistrationModel;
  return registration;
}

export function mapModelToSubmitRequest(model: IBn9RegistrationModel): IBn9RegistrationSubmitModel {
  const shouldBeIncluded = ['1', '5'].indexOf(model.scenario) > -1;
  const businessContacts = mapContactsModelToSubmitRequest(model.contacts);
  const businessOwnership = mapOwnersModelToSubmitRequest(model.owners);
  const businessActivity = shouldBeIncluded ? mapBusinessActivityModelToSubmitRequest(model.businessActivity) : undefined;
  const legalEntity = shouldBeIncluded ? mapLegalEntityModelToSubmitRequest(model.legalEntity) : undefined;
  const legalName = shouldBeIncluded ? mapLegalNameModelToSubmitRequest(model.legalName) : undefined;
  const businessAddresses = shouldBeIncluded ? mapAddressModelToSubmitRequest(model.address) : undefined;
  const modelToSubmit = {
    businessNumber: model?.businessNumber,
    programType: model?.programType,
    legalEntity,
    businessOwnership,
    legalName,
    businessActivity,
    businessAddresses,
    businessContacts,
    scenario: model?.scenario
  } as IBn9RegistrationSubmitModel;

  return modelToSubmit;
}

function mapLegalEntityModelToSubmitRequest(model: ILegalEntityModel): ILegalEntitySubmitModel {
  if (!model) {
    return null;
  }

  if (model.jurisdiction !== 'Foreign') {
    model.jurisdictionCountry = 'CA'; // for Canada
  }
  return {
    ...model,
    incorporationDate: !!model.incorporationDate && dayjs(model.incorporationDate).isValid() ?
                       dayjs(model.incorporationDate).format('YYYY-MM-DD') : null,
  } as ILegalEntitySubmitModel;
}

function mapOwnersModelToSubmitRequest(model: IOwnersModel): IBusinessOwnershipSubmitModel {
  const authorizedOfficialsList: IContactSubmitModel[] = [];
  const official1 = mapContactToSubmitRequest(model?.authorizedOfficial1, true);
  const official2 = mapContactToSubmitRequest(model?.authorizedOfficial2, true);
  authorizedOfficialsList.push(official1);
  if (!Object.values(official2).every(el => !el)) {
    authorizedOfficialsList.push(official2);
  }

  return {
    businessOwnershipList: model?.owners?.map(owner => mapOwnerModelToSubmitRequest(owner)),
    authorizedOfficialsList,
  } as IBusinessOwnershipSubmitModel;
}

function mapOwnerModelToSubmitRequest(model: IOwner): IOwnershipSubmitModel {
  return {
    position: model.position,
    firstName: model.firstName,
    lastName: model.lastName,
    email: model.email,
    language: model.languagePref,
    domesticPhoneList: mapDomesticPhonesModelToSubmitRequest(model.domesticPhones),
    internationalPhoneList: mapWorldwidePhonesModelToSubmitRequest(model.worldwidePhones),
  } as IOwnershipSubmitModel;
}

function mapLegalNameModelToSubmitRequest(model: ILegalNameModel): ILegalNameSubmitModel {
  return {
    ...model,
  } as ILegalNameSubmitModel;
}

function mapAddressModelToSubmitRequest(model: IAddressModel): IBusinessAddressSubmitModel {
  return {
    businessGenericContactInfo: mapGenericContactInfo(model),
    physicalAddress: mapIAddressToBn9AddressRequest(model?.physicalAddress),
    mailingAddress: mapIAddressToBn9AddressRequest(model?.mailingAddress),
    recordsAddress: mapIAddressToBn9AddressRequest(model?.recordsAddress, model?.booksAndRecordsRelationToPrograms),
    careOf: model?.careOf,
    sameMailingAndPhysicalAddress: model?.sameAsPhysical,
    sameAddressAs: model?.sameAsAddress,
  } as IBusinessAddressSubmitModel;
}

function mapGenericContactInfo(model: IAddressModel): IGenericContactInfoSubmitModel {
  if (!model) {
    return undefined;
  }
  return {
    email: model?.email,
    phone: model?.telIntFormGroup?.e164Phone,
    fax: model?.fax?.replace(/-/g, ''),
    extension: model?.extension,
    website: model?.website,
    languagePreference: model?.languagePreference,
    correspondencePreference: model?.correspondencePreference
  } as IGenericContactInfoSubmitModel;
}

function mapIAddressToBn9AddressRequest(model: IAddress, brAddressRemark?: string): IAddressSubmitModel {
  return {
    careOf: model?.careOf,
    line1: model?.line1,
    line2: model?.line2,
    town: model?.town,
    poBox: model?.poBox?.toString(),
    country: model?.carmCountry,
    postalCode: model?.postalCode?.toUpperCase()?.toNullIfEmpty(),
    region: model?.carmRegion,
    phone: model?.phone?.removeHyphens(),
    brAddressRemark
  } as IAddressSubmitModel;
}

function mapDomesticPhonesModelToSubmitRequest(phones: IPhone[]): IBusinessPhonesSubmitModel {
  return {
    phoneList: phones.filter(p => p.telIntFormGroup?.phoneNumber)?.map(phone => mapDomesticPhoneModelToSubmitRequest(phone)),
  } as IBusinessPhonesSubmitModel;
}

function mapDomesticPhoneModelToSubmitRequest(model: IPhone): IPhoneSubmitModel {
  return {
    phoneType: model?.telephoneType,
    deviceType: model?.deviceType,
    areaCode: model?.areaCode,
    cityCode: model?.telIntFormGroup?.e164Phone?.toString().substring(2, 5),
    phoneNumber: model?.telIntFormGroup?.e164Phone?.toString().substring(5),
    phoneExtension: model?.telephoneExtensionNumber,
  } as IPhoneSubmitModel;
}

function mapWorldwidePhonesModelToSubmitRequest(phones: IPhone[]): IBusinessPhonesSubmitModel {
  return {
    phoneList: phones.filter(p => p.telIntFormGroup)?.map(phone => mapWorldwidePhoneModelToSubmitRequest(phone)),
  } as IBusinessPhonesSubmitModel;
}

function mapWorldwidePhoneModelToSubmitRequest(model: IPhone): IPhoneSubmitModel {
  return {
    phoneType: model?.telephoneType,
    deviceType: model?.deviceType,
    areaCode: model?.areaCode,
    countryCode: model?.telIntFormGroup?.countryDialCode?.iso2,
    cityCode: model?.telIntFormGroup?.cityCode,
    phoneNumber: model?.telIntFormGroup?.phoneNumber,
    phoneExtension: model?.telephoneExtensionNumber,
  } as IPhoneSubmitModel;
}

function mapContactsModelToSubmitRequest(model: IContactsModel): IBusinessContactsSubmitModel {
  return {
    businessGenericContactInfo: mapGenericContactInfo(model.genericContactInfo),
    businessContactList: model?.contacts?.map(contact => mapContactToSubmitRequest(contact)),
  } as IBusinessContactsSubmitModel;
}

export function mapContactToSubmitRequest(model: IContact, isTextTitle?: boolean): IContactSubmitModel {
  return {
    contactId: model?.contactId,
    email: model?.email,
    contactType: model?.contactType,
    otherContactType: model?.otherContactType,
    title: isTextTitle === true ? undefined : model?.contactTitle,
    otherContactTitle: isTextTitle === true ? undefined : model?.otherContactTitle,
    fax: model?.fax?.replace(/-/g, ''),
    firstName: model?.firstName,
    lastName: model?.lastName,
    middleName: model?.middleName,
    language: model?.languagePref,
    telephone: model?.telIntFormGroup.e164Phone,
    extension: model?.extension,
    authorizedOfficialTitle: isTextTitle === true ? model?.contactTitle : undefined
  } as IContactSubmitModel;
}

export function mapBusinessOfficialToSubmitRequest(model: IBusinessOfficial): IContactSubmitModel {
  return {
    contactId: model?.officialId,
    email: model?.email,
    contactType: model?.type,
    otherContactType: null,
    title: model?.position,
    fax: model?.fax,
    firstName: model?.firstName,
    lastName: model?.lastName,
    middleName: model?.middleName,
    language: model?.languagePref,
    telephone: model?.telephone,
    extension: model?.extension,
  } as IContactSubmitModel;
}

function mapBusinessActivityModelToSubmitRequest(model: IBusinessActivityModel): IBusinessActivitySubmitModel {
  return {
    businessActivity: model?.majorActivity?.toNullIfEmpty(),
    mainServiceProductDes1: model?.mainProduct1?.toNullIfEmpty(),
    mainServiceProductDes2: model?.mainProduct2?.toNullIfEmpty(),
    mainServiceProductDes3: model?.mainProduct3?.toNullIfEmpty(),
    percentRevProductService1: model?.percentRevenue1?.toNullIfEmpty(),
    percentRevProductService2: model?.percentRevenue2?.toNullIfEmpty(),
    percentRevProductService3: model?.percentRevenue3?.toNullIfEmpty(),
  } as IBusinessActivitySubmitModel;
}

export function mapDraftSubmitToModel(savedDraft: IBusinessRegistrationDraftResponse): IBn9RegistrationModel {
  const businessRegistration = savedDraft.businessRegistration;
  const uid = businessRegistration?.uid;
  const draft = businessRegistration?.draft;

  return {
    uid,
    scenario: businessRegistration?.scenario,
    hasPaymentAmount: businessRegistration?.hasPaymentAmount,
    hasSOATotals: businessRegistration?.hasSOATotals,
    hasTransactions: businessRegistration?.hasTransactions,
    skipAffinityQuestions: (businessRegistration?.skipAffinityQuestions === true ||
                            businessRegistration?.transactionsValidationStatus === true),
    transactionNumber: businessRegistration?.transactionNumber,
    bn9: businessRegistration?.bn9,
    businessNumber: businessRegistration?.bn9,
    programNumber: businessRegistration?.programNumber,
    programType: businessRegistration?.programType || businessRegistration?.type,
    programSubType: businessRegistration?.programSubType,
    programId: businessRegistration?.cbsaLegacyProgramId,
    businessLegalName: businessRegistration?.businessLegalName,
    businessOperatingName: businessRegistration?.businessOperatingName,
    caseNumber: businessRegistration?.caseNumber,
    // draft: string;
    lastModifiedDate: businessRegistration?.lastModifiedDate,
    // name: string;
    status: businessRegistration?.status,
    // type: string;
    hasSystemErrors: businessRegistration?.hasSystemErrors,
    legalEntityValidationStatus: businessRegistration?.legalEntityValidationStatus,
    submittedDate: businessRegistration?.submittedDate,
    ...(!!draft ? JSON.parse(draft) : undefined)
  } as IBn9RegistrationModel;
}

export function mergeRegistrationAndValidationInfo(registration: IBn9RegistrationModel, draftModel: IValidateBusinessModel): IBn9RegistrationModel {
  return {
    ...registration,
    legalEntity: {
      businessStructure: draftModel?.businessStructure,
      jurisdiction: draftModel?.jurisdiction,
      jurisdictionCountry: draftModel?.jurisdictionCountry,
      jurisdictionProvince: draftModel?.jurisdictionProvince,
      jurisdictionState: draftModel?.jurisdictionState,
      incorporationCertificateId: draftModel?.incorporationCertificateId,
      incorporationDate: draftModel?.incorporationDate
    },
    legalName: {
      legalName: draftModel?.legalEntityName
    } as ILegalNameModel,
    address: {
      mailingAddress: {
        carmCountry: draftModel?.address?.country,
        carmRegion: draftModel?.address?.provinceOrState,
        town: draftModel?.address?.city,
        line1: draftModel?.address?.line1,
        line2: draftModel?.address?.line2,
        postalCode: draftModel?.address?.postalOrZipCode
      }
    },
  } as IBn9RegistrationModel;
}

export function getTransactionInfoSubmitModel(model: IValidateBusinessModel): ITransactionInfoSubmitModel {
  return {
    businessNumber: model.businessNumber,
    programNumber: model.programNumber ? 'RM' + model.programNumber : undefined,
    programId: model.programId,
    programType: model.programType,
  } as ITransactionInfoSubmitModel;
}

export function getValidateBusinessInfoSubmitModel(model: IValidateBusinessModel): IValidateBusinessInformationSubmitModel {
  const programType = mapRegistrationProgramTypeToProgramEnrolmentType.get(model.programType as RegistrationProgramType);
  return {
    scenario: model.scenario,
    businessNumber: model.businessNumber,
    programNumber: model.programNumber ? 'RM' + model.programNumber : undefined,
    programId: model.programId,
    programType,
    legalEntityName: model.legalEntityName,
    businessStructure: model.businessStructure,
    jurisdiction: model.jurisdiction,
    jurisdictionProvince: model.jurisdictionProvince,
    jurisdictionCountry: model.jurisdictionCountry,
    jurisdictionState: model.jurisdictionState,
    incorporationCertificateId: model.incorporationCertificateId,
    incorporationDate: model.incorporationDate,
    address: {
      line1: model.address.line1,
      line2: model.address.line2,
      town: model.address.city,
      country: model.address.country,
      region: model.address.provinceOrState,
      postalCode: model.address.postalOrZipCode,
    },
    certification: model.certification,
  } as IValidateBusinessInformationSubmitModel;
}

export function mapProgramsToBusinessProgram(program: IProgramResponse, notAvailableText: string): ILinkBusinessProgram {
  return {
    programId: (!program.programAreaId ? notAvailableText : program.programAreaId).extractProgramCode(program?.type !== ProgramTypes.CustomsBroker),
    programName: program.typeName || program?.operatingName || program?.name,
    programNumber: program?.uid,
  } as ILinkBusinessProgram;
}
